/**
 * Cute WebAudio-synthesized sound effects for quiz UIs (ROO-233).
 *
 * Synth tones (no audio-file assets) keep the bundle light and sidestep any
 * licensing questions. The AudioContext is created lazily on first call from
 * a real user gesture — browsers block autoplay before that — and every
 * export below is a safe no-op when audio is unavailable (SSR, jsdom/test
 * environments, browsers without WebAudio) or the user has muted sounds.
 */

const MUTE_KEY = 'cleveroo_sound_muted';
// Keyboard/mouse sweeps across several options shouldn't machine-gun the
// hover blip — only replay it once this many ms have passed.
const HOVER_DEBOUNCE_MS = 60;

type MuteListener = (muted: boolean) => void;

let audioCtx: AudioContext | null = null;
let muted = loadMuted();
let lastHoverAt = 0;
const listeners = new Set<MuteListener>();

function loadMuted(): boolean {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem(MUTE_KEY) === '1';
  } catch {
    return false;
  }
}

function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const Ctor: typeof AudioContext | undefined =
    window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  if (!audioCtx) {
    try {
      audioCtx = new Ctor();
    } catch {
      return null;
    }
  }
  if (audioCtx.state === 'suspended') {
    // Only succeeds when called from within a user-gesture handler (click,
    // keydown, etc) — exactly the contexts playHover/playSelect are called
    // from, so this quietly unlocks audio the first time the user interacts.
    audioCtx.resume().catch(() => {});
  }
  return audioCtx;
}

/** One short tone: a sine/triangle blip with a soft attack/decay envelope. */
function playTone(freq: number, startOffset: number, duration: number, peakGain: number, type: OscillatorType = 'sine'): void {
  const ctx = getAudioContext();
  if (!ctx) return;
  try {
    const now = ctx.currentTime + startOffset;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, now);
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(peakGain, now + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(now);
    osc.stop(now + duration + 0.02);
  } catch {
    // Never let a sound glitch break the quiz — audio is best-effort.
  }
}

/** Very soft, short blip for hovering/focusing an answer option (~40ms). */
export function playHover(): void {
  if (muted) return;
  const t = Date.now();
  if (t - lastHoverAt < HOVER_DEBOUNCE_MS) return;
  lastHoverAt = t;
  playTone(880, 0, 0.04, 0.045, 'sine');
}

/** Brighter, satisfying chime for selecting/committing an answer. */
export function playSelect(): void {
  if (muted) return;
  playTone(659.25, 0, 0.09, 0.09, 'triangle');
  playTone(987.77, 0.035, 0.1, 0.07, 'triangle');
  hapticTap('light');
}

/** Tiny success arpeggio for a correct-answer reveal. */
export function playSuccess(): void {
  if (muted) return;
  [523.25, 659.25, 783.99, 1046.5].forEach((freq, i) => {
    playTone(freq, i * 0.07, 0.16, 0.07, 'sine');
  });
  hapticTap('medium');
}

// Native haptics in the Capacitor iOS shell (ROO-235). Dynamic import keeps
// the web bundle path inert; every call is fail-soft.
function hapticTap(strength: 'light' | 'medium'): void {
  import('@capacitor/core')
    .then(({ Capacitor }) => {
      if (!Capacitor.isNativePlatform()) return;
      return import('@capacitor/haptics').then(({ Haptics, ImpactStyle }) =>
        Haptics.impact({ style: strength === 'light' ? ImpactStyle.Light : ImpactStyle.Medium })
      );
    })
    .catch(() => {});
}

export function isMuted(): boolean {
  return muted;
}

export function setMuted(next: boolean): void {
  if (muted === next) return;
  muted = next;
  try {
    localStorage.setItem(MUTE_KEY, next ? '1' : '0');
  } catch {
    // storage unavailable (private mode / SSR) — mute state just won't persist
  }
  listeners.forEach((l) => l(muted));
}

export function toggleMuted(): void {
  setMuted(!muted);
}

/** Subscribe to mute-state changes (any surface can flip it live). Returns an unsubscribe fn. */
export function subscribe(listener: MuteListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
